(ns machtfit.product-form.what
  (:require [clojure.set :as set]
            [clojure.string :as str]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.materialize-widgets :as materialize-widgets]
            [machtfit.product-form.core :as product-form]
            [machtfit.product-form.fileupload :as fileupload]
            [machtfit.product-form.offer-layers :as offer-layers]
            [machtfit.product-form.review :as review]
            [machtfit.product-form.section :refer [show-next-section]]
            [machtfit.product-form.where :refer [website]]
            [machtfit.startpage.statics :as statics]
            [machtfit.utils :refer [compare-by in? ceil-to-decimal delete-at]]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(rf/reg-event-db ::set-title
  (fn [db [_ new-title]]
    (-> db
        (assoc-in [:product :what :title] new-title)
        (assoc-in [:ui :last-focused-config-section] nil))))

(defn- image-usage-text [usages]
  (case usages
    0 "Das Bild wird noch nicht genutzt"
    1 "1 Angebot nutzt dieses Bild"
    (str usages " Angebote nutzen dieses Bild")))

(defn- title []
  (let [title' @(rf/subscribe [:get [:product :what :title]])]
    [:div.col.s12 {:style {:margin-bottom "1rem"}}
     [widgets/block-input2
      {:id "title"
       :label-text "Titel"
       :placeholder "Bitte geben Sie einen Titel ein"
       :value title'
       :autofocus? true
       :on-save #(rf/dispatch [::set-title %])
       :block-style {:width "100%"}
       :style {:font-size "2rem"}
       :label-style {:font-size "1.2rem"}}]
     (when (> (count title') 255)
       [:span>i {:style {:color "red"}}
        "Der Titel ist zu lang. Es dürfen nur maximal 255 Zeichen verwendet werden."])]))

(defn- offer-layers-toggle []
  [:div.col.s12 {:style {:margin "1.5rem 0"}}
   [:input.filled-in
    {:id "offer-layers-enabled"
     :type "checkbox"
     :checked @(rf/subscribe [:get [:product :what :offer-layers :enabled?]])
     :on-change #(rf/dispatch [::offer-layers/toggle-enabled])}]
   [:label {:for "offer-layers-enabled" :style {:color "black"}}
    "Detaillierte Angebotsarchitektur definieren"]])

(defn- partner []
  (let [{:keys [is_supplier
                is_place_of_business
                is_company
                url
                name]} @(rf/subscribe [:get [:ui :partner]])]
    [:div.col.s12
     [:div.center-align
      [:span (cond
               is_supplier "Angebot des Anbieters: "
               is_place_of_business "Angebot der Niederlassung: "
               is_company "Angebot des Unternehmens: "
               :else "Angebot von Partner: ")]
      [:a {:href url
           :target "_blank"}
       name]]]))

(rf/reg-sub ::current-image
  :<- [:get [:images]]
  :<- [:get [:product :what :image-id]]

  (fn [[images current-image-id] _]
    (when (some? current-image-id)
      (first (filter #(= current-image-id (:id %)) images)))))

(rf/reg-sub ::current-image-by-id
  :<- [:get [:images]]

  (fn [images [_ current-image-id]]
    (when (some? current-image-id)
      (first (filter #(= current-image-id (:id %)) images)))))

(rf/reg-sub ::current-image-id-by-position
  :<- [:get [:product :what :images]]
  :<- [:get [:ui :current-image-position]]

  (fn [[product-images current-position] _]
    (when (seq product-images)
      (:image-id (first (filter #(= current-position (:display-order %)) product-images))))))

(defn- images-in-category [images current-category current-image category]
  (let [is-current-category? (= current-category category)
        base-set (set (filter #(in? (:categories %) category) images))
        add-current-image? (and is-current-category? (some? current-image) (not (contains? base-set current-image)))]
    (cond-> base-set
      add-current-image? (conj current-image))))

(rf/reg-sub ::images-in-category
  :<- [:get [:images]]
  :<- [:get [:product :what :category]]
  :<- [::current-image]

  (fn [[images current-category current-image] [_ category]]
    (images-in-category images current-category current-image category)))

(rf/reg-sub ::images-in-category?
  (fn [[_ category]]
    [(rf/subscribe [::images-in-category category])])

  (fn [[images-in-category'] _]
    (not (empty? images-in-category'))))

(rf/reg-sub ::partner-images
  :<- [:get [:images]]
  :<- [::current-image]

  (fn [[images current-image] _]
    (let [base-set (filter #(= (:source %) :partner) images)
          add-current-image? (and (= (:source current-image) :partner) (every? #(not= (:id current-image) (:id %)) base-set))]
      (cond-> base-set
        add-current-image? (conj current-image)))))

(rf/reg-sub ::partner-images?
  :<- [::partner-images]

  (fn [partner-images _]
    (not (empty? partner-images))))

(defn- set-main-image [db image-id]
  (assoc-in db [:product :what :image-id] image-id))

(defn- set-image-media [db image-id]
  (let [images @(rf/subscribe [:get [:product :what :images]])
        current-position @(rf/subscribe [:get [:ui :current-image-position]])
        current-image (first (filter #(= (:display-order %) current-position) images))
        db* (assoc-in db [:product :what :images] (if (seq current-image)
                                                    (map #(if (= (:display-order %) current-position)
                                                            (assoc % :image-id image-id)
                                                            %)
                                                         images)
                                                    (conj images {:image-id image-id
                                                                  :display-order current-position})))]
    (if (= current-position 1)
      (set-main-image db* image-id)
      db*)))

(defn- remove-image [db image-id display-order]
  (let [images (get-in db [:product :what :images])
        rerrange-display-order (fn [images]
                                 (map-indexed (fn [idx image] (assoc image :display-order (inc idx)))
                                              (sort-by :display-order images)))
        updated-images (rerrange-display-order
                        (remove
                         #(and
                           (contains? % :image-id)
                           (= (:image-id %) image-id)
                           (= (:display-order %) display-order))
                         images))
        db* (set-main-image db (:image-id (first (sort-by :display-order updated-images))))]
    (assoc-in db* [:product :what :images] updated-images)))

(defn- set-stock-image-if-needed [current-db category]
  (if-not (get-in current-db [:ui :image-set-manually?])
    (let [images-in-category' (images-in-category (:images current-db)
                                                  (:product-what-category current-db)
                                                  (:current-image current-db)
                                                  category)
          image (when-not (empty? images-in-category') (rand-nth (into [] images-in-category')))]
      (-> current-db
          (set-main-image (:id image))
          (assoc-in [:product :what :images]
                    [{:image-id (:id image)
                      :display-order 1}])))
    current-db))

(defn- reset-search-keywords-by-category [db category old-category]
  (let [current-search-keywords (get-in db [:product :what :search-keywords])
        current-search-keywords-for-category (set (get-in db [:search-keywords (keyword old-category)]))
        cleaned-search-keywords (set/difference current-search-keywords current-search-keywords-for-category)
        new-search-keywords-for-category (set/union cleaned-search-keywords (set (get-in db [:search-keywords (keyword category)])))]
    (assoc-in db [:product :what :search-keywords] new-search-keywords-for-category)))

(rf/reg-event-db ::set-category
  (fn [db [_ new-category]]
    (let [old-category (get-in db [:product :what :category])
          is-company-offer? (get-in db [:ui :partner :is_company])
          db* (if is-company-offer?
                db
                (reset-search-keywords-by-category db new-category old-category))]

      (-> db*
          (assoc-in [:product :what :category] new-category)
          (assoc-in [:ui :last-focused-config-section] nil)
          (assoc-in [:ui :image-section-expanded :partner-images] (not (some? new-category)))
          (assoc-in [:ui :image-section-expanded :current-category] (some? new-category))
          (set-stock-image-if-needed new-category)))))

(rf/reg-event-db ::set-new-category
  (fn [db [_ new-category]]
    (-> db
        (assoc-in [:ui :new-category] new-category)
        (assoc-in [:product :what :category] new-category))))

(rf/reg-event-db ::select-new-category
  (fn [db _]
    (-> db
        (assoc-in [:product :what :category] (get-in db [:ui :new-category]))
        (set-stock-image-if-needed nil))))

(defn- new-category []
  (let [new-category' @(rf/subscribe [:get [:ui :new-category]])
        current-category @(rf/subscribe [:get [:product :what :category]])]
    [:div
     [widgets/radio-option
      {:name "category"
       :label-text [widgets/inline-input
                    {:placeholder "Neue Kategorie"
                     :value new-category'
                     :on-save #(rf/dispatch [::set-new-category %])
                     :style {:text-align "left"
                             :width "inherit"}}]
       :checked? (= current-category new-category')
       :on-select #(rf/dispatch [::select-new-category])}]]))

(defn- existing-categories
  "Categories grouped under 'Aktivitäten' and 'Produkte' headings."
  []
  (let [categories (sort @(rf/subscribe [:get [:categories]]))
        current-value @(rf/subscribe [:get [:product :what :category]])
        activity-categories (seq (remove #(contains? statics/product-categories %) categories))
        product-categories (seq (filter #(contains? statics/product-categories %) categories))]
    [:div
     (when activity-categories
       [:<>
        [:h6 {:style {:margin-top "0.5rem" :margin-bottom "0.5rem" :font-weight "bold"}}
         "Aktivitäten"]
        [:div {:style {:-webkit-column-count 2
                       :-moz-column-count 2
                       :column-count 2
                       :margin-bottom "1rem"}}
         [widgets/radio-options
          {:name "category"
           :current-value current-value
           :choices (map (fn [c] [c c]) activity-categories)
           :on-select #(rf/dispatch [::set-category %])}]]])
     (when product-categories
       [:<>
        [:h6 {:style {:margin-top "0.5rem" :margin-bottom "0.5rem" :font-weight "bold"}}
         "Produkte"]
        [:div {:style {:-webkit-column-count 2
                       :-moz-column-count 2
                       :column-count 2}}
         [widgets/radio-options
          {:name "category"
           :current-value current-value
           :choices (map (fn [c] [c c]) product-categories)
           :on-select #(rf/dispatch [::set-category %])}]]])]))

(defn- categories []
  [:div {:style {:padding-top "1rem"}}
   [:div.input-field [:label.active {:style {:padding-bottom "0.4rem"}}
                      "Kategorie"]]
   [existing-categories]
   (when @(rf/subscribe [:get [:options :can-create-category?]]) [new-category])])

(rf/reg-event-db ::set-description
  (fn [db [_ new-description]]
    (-> db
        (assoc-in [:product :what :description] new-description)
        (assoc-in [:ui :last-focused-config-section] nil))))

(rf/reg-event-db ::remove-keyword
  (fn [db [_ kw]]
    (-> db
        (update-in [:product :what :search-keywords] #(disj % kw)))))

(defn coerce-search-keywords [search-keyword]
  (-> search-keyword
      .toLowerCase
      str/trim
      (str/replace #"[^ a-zäüöß0-9]" "")
      (str/replace #"\s+" " ")
      (str/split #"\s+")))

(def search-keyword-min-length 3)
(def search-keyword-max-length 50)
(def search-keyword-max-count 15)

(rf/reg-event-db ::add-search-keyword
  (fn [db [_ search-keyword]]
    (let [coerced-search-keywords (coerce-search-keywords search-keyword)
          valid-search-keyword? (fn [coerced-search-keyword]
                                  (and (>= (count coerced-search-keyword) search-keyword-min-length)
                                       (<= (count coerced-search-keyword) search-keyword-max-length)))
          new-search-keywords (set (filter valid-search-keyword? coerced-search-keywords))]
      (update-in db [:product :what :search-keywords] set/union new-search-keywords))))

(rf/reg-sub ::sorted-search-keywords
  :<- [:get [:product :what :search-keywords]]
  (fn [search-keywords _]
    (sort search-keywords)))

(rf/reg-sub ::search-keyword-count-maxed?
  :<- [:get [:product :what :search-keywords]]
  (fn [search-keywords _]
    (>= (count search-keywords) search-keyword-max-count)))

(defn- add-search-keywords-on-key-up [element]
  (when (= (.-key element) "Enter")
    (rf/dispatch-sync [::add-search-keyword (-> element .-target .-value)])
    (set! (-> element .-target .-value) "")))

(defn- keyword-input []
  (if @(rf/subscribe [::search-keyword-count-maxed?])
    [:div (str "Maximal " search-keyword-max-count " Schlagwörter sind erlaubt.")]
    [:input
     {:type "text"
      :maxLength search-keyword-max-length
      :on-key-up #(add-search-keywords-on-key-up %)
      :placeholder (str "Schlagwort hinzufügen ("
                        search-keyword-min-length "–"
                        search-keyword-max-length " Zeichen)")}]))

(defn- search-keywords []
  (when @(rf/subscribe [:get [:ui :partner :is_supplier]])
    (let [current-search-keywords @(rf/subscribe [::sorted-search-keywords])]
      [:div {:style {:padding-top "2rem" :padding-bottom "1rem"}}
       [:div.input-field [:label.active {:style {:padding-bottom "0.4rem"}}
                          "Schlagwörter"]]
       (for [search-keyword current-search-keywords]
         [:span
          {:style {:border-radius "5px"
                   :padding "6px"
                   :margin-right "2px"
                   :line-height "28px"
                   :color "white"
                   :white-space "nowrap"
                   :background-color "#34b78f"}
           :key search-keyword}
          search-keyword " "
          [:button {:on-click #(rf/dispatch [::remove-keyword search-keyword])
                    :style {:border "none"}} "X"]])
       [keyword-input]])))

(rf/reg-sub ::sorted-ai-search-keywords
  :<- [:get [:product :what :ai-search-keywords]]
  (fn [ai-search-keywords _]
    (sort-by :term ai-search-keywords)))

(rf/reg-sub ::ai-tag-status
  :<- [:get [:product :what :ai-tag-status]]
  (fn [status _]
    status))

(defn- ai-search-keywords-empty-state [status]
  (case status
    "failed" [:div {:style {:color "#c62828"}}
              [:em "KI-Schlagwörter konnten nicht generiert werden. Bitte das Dev-Team oder Product kontaktieren, falls das Problem bestehen bleibt."]]
    "refused" [:div {:style {:color "grey"}}
               [:em "Für dieses Angebot wurden keine KI-Schlagwörter erzeugt."]]
    ;; "tagged" reaches the empty state only when every generated keyword was
    ;; blocked/removed via the admin denylist.
    "tagged" [:div {:style {:color "grey"}}
              [:em "Alle KI-Schlagwörter wurden blockiert. Diese können in der Blockliste editiert werden."]]
    "pending" [:div {:style {:color "grey"}}
               [:em "KI-Schlagwörter werden noch generiert …"]]
    ;; "not_generated", plus nil on the create form (no tagging state yet)
    [:div {:style {:color "grey"}} [:em "KI-Schlagwörter wurden noch nicht generiert."]]))

;; AI search keywords are read-only here: they are generated and curated through a
;; separate Django admin interface, so the dashboard only displays them. Direct
;; editing is omitted on purpose (a removed keyword could be regenerated anyway).
(defn- ai-search-keywords []
  (let [current-ai-search-keywords @(rf/subscribe [::sorted-ai-search-keywords])
        status @(rf/subscribe [::ai-tag-status])
        admin-url @(rf/subscribe [:get [:ui :admin-aikeyword-changelist-url]])]
    [:div {:style {:padding-top "2rem" :padding-bottom "1rem"}}
     [:div.input-field [:label.active {:style {:padding-bottom "0.4rem"}}
                        "KI-Schlagwörter"]]
     (if (seq current-ai-search-keywords)
       [:div
        ;; The shown keywords are the previous set: the content changed since
        ;; they were generated ("pending"), or a re-tag errored out ("failed").
        (case status
          "pending" [:div {:style {:color "grey" :padding-bottom "0.4rem"}}
                     [:em "KI-Schlagwörter werden aktualisiert …"]]
          "failed" [:div {:style {:color "#c62828" :padding-bottom "0.4rem"}}
                    [:em "Aktualisierung der KI-Schlagwörter fehlgeschlagen. Es werden die zuletzt generierten angezeigt."]]
          nil)
        (into [:div {:style {:display "flex" :flex-wrap "wrap" :gap "4px"}}]
              (for [{:keys [term]} current-ai-search-keywords]
                [:span
                 {:style {:border-radius "5px"
                          :padding "6px"
                          :line-height "28px"
                          :color "white"
                          :white-space "nowrap"
                          :background-color "#78909c"}
                  :key term}
                 term]))]
       [ai-search-keywords-empty-state status])
     (when admin-url
       [:a.waves-effect.waves-light.btn
        {:href admin-url
         :target "_blank"
         :rel "noopener noreferrer"
         :style {:margin-top "1rem"}}
        "KI-Schlagwörter bearbeiten"])]))

(rf/reg-event-db ::remove-key-fact
  (fn [db [_ kf]]
    (-> db
        (update-in [:product :what :key-facts] #(disj % kf)))))

(def key-fact-min-length 3)
(def key-fact-max-length 50)
(def key-fact-max-count 10)

(rf/reg-event-db ::add-key-fact
  (fn [db [_ key-fact]]
    (let [valid-key-fact? (fn [key-fact]
                            (and (>= (count key-fact) key-fact-min-length)
                                 (<= (count key-fact) key-fact-max-length)))]
      (when (valid-key-fact? key-fact)
        (update-in db [:product :what :key-facts] set/union #{key-fact})))))

(rf/reg-sub ::sorted-key-facts
  :<- [:get [:product :what :key-facts]]
  (fn [key-facts _]
    (sort key-facts)))

(rf/reg-sub ::key-fact-count-maxed?
  :<- [:get [:product :what :key-facts]]
  (fn [search-key-facts _]
    (>= (count search-key-facts) key-fact-max-count)))

(defn- add-key-facts-on-key-up [element]
  (when (= (.-key element) "Enter")
    (rf/dispatch-sync [::add-key-fact (-> element .-target .-value)])
    (set! (-> element .-target .-value) "")))

(defn- key-fact-input []
  (if @(rf/subscribe [::key-fact-count-maxed?])
    [:div (str "Maximal " key-fact-max-count " Schlüsselfakten sind erlaubt.")]
    [:input
     {:type "text"
      :maxLength key-fact-max-length
      :on-key-up #(add-key-facts-on-key-up %)
      :placeholder (str "Schlüsselfakten hinzufügen ("
                        key-fact-min-length "–"
                        key-fact-max-length " Zeichen)")}]))

(defn- key-facts []
  (let [current-key-facts @(rf/subscribe [::sorted-key-facts])]
    [:div {:style {:padding-top "2rem" :padding-bottom "1rem"}}
     [:div.input-field [:label.active {:style {:padding-bottom "0.4rem"}}
                        "Schlüsselfakten"]]
     (for [key-fact current-key-facts]
       [:span
        {:style {:border-radius "5px"
                 :padding "6px"
                 :margin-right "2px"
                 :line-height "28px"
                 :color "white"
                 :white-space "nowrap"
                 :background-color "#34b78f"}
         :key key-fact}
        key-fact " "
        [:button {:on-click #(rf/dispatch [::remove-key-fact key-fact])
                  :style {:border "none"}} "X"]])
     [key-fact-input]]))

(defn- description []
  [:div {:style {:padding-top "2rem" :padding-bottom "1rem"}}
   [:div.input-field [:label.active {:style {:padding-bottom "0.4rem"}}
                      "Beschreibung"]]
   [widgets/quill {:value @(rf/subscribe [:get [:product :what :description]])
                   :on-change #(rf/dispatch [::set-description %])}]
   (when @(rf/subscribe [::product-form/description-too-big?])
     [:span>i {:style {:color "red"}}
      "Der Beschreibungstext ist zu groß. Benutzen Sie ggfs. besser komprimierte, weniger oder kleinere Bilder.
       Alle Bilder zusammen sollten nicht größer als ungefähr 750 KB sein."])])

(defn- health-relevance-text []
  (let [value @(rf/subscribe [:get [:product :what :health-relevance-text]])]
    [:div.pt-8.pb-4
     [widgets/textarea
      {:id "health-relevance-text"
       :label-text "Gesundheitsbezug Text"
       :value value
       :maxLength 1024
       :on-change #(rf/dispatch [:set-path [:product :what :health-relevance-text] %])}]
     (when (> (count (or value "")) 1024)
       [:span>i {:style {:color "red"}}
        "Der Gesundheitsbezug Text ist zu lang. Es dürfen nur maximal 1024 Zeichen verwendet werden."])]))

(defn- select-image-media [current-db image-id]
  (-> current-db
      (set-image-media image-id)
      (assoc-in [:ui :image-media-library-active?] false)
      (fileupload/reset-error nil)
      (assoc-in [:ui :image-set-manually?] true)))

(rf/reg-event-db ::select-image
  (fn [db [_ image-id]]
    (select-image-media db image-id)))

(rf/reg-event-db ::toggle-image-library-active
  (fn [db [_ display-order]]
    (-> db
        (assoc-in [:ui :current-image-position] display-order)
        (fileupload/reset-error display-order)
        (update-in [:ui :image-media-library-active?] not))))

(rf/reg-event-db ::add-and-select-image
  (fn [db [_ image]]
    (let [images (:images db)
          already-exists? (some #(= (:id image) (:id %)) images)]
      (-> db
          (assoc :images (cond-> images (not already-exists?) (conj image)))
          (select-image-media (:id image))))))

(rf/reg-event-db ::remove-image
  (fn [db [_ image-id display-order]]
    (remove-image db image-id display-order)))

(defn- image [show-action image-id display-order]
  (let [current-image @(rf/subscribe [::current-image-by-id image-id])
        image-url (:url current-image)
        on-click (when show-action #(rf/dispatch [::toggle-image-library-active display-order]))
        on-click-remove (when show-action #(rf/dispatch [::remove-image image-id display-order]))
        machtfit-admin? @(rf/subscribe [::product-form/machtfit-admin?])]
    [:div.card.center
     [:div.card-image {:style {:padding "0.5rem"}}
      (if (some? image-url)
        [:<>
         [:img {:src image-url
                :on-click on-click
                :style (when show-action {:cursor "pointer"})}]
         (when machtfit-admin?
           [:p (image-usage-text (:usages current-image))])]
        [:div {:style {:color "grey"}}
         [:div
          {:on-click (when show-action on-click)
           :style {:font-size "100px" :cursor "pointer"}}
          [:i.icon-picture]]
         [:div
          [:em "kein Bild"]]])
      [:div.card-action
       (when (not show-action) {:style {:opacity 0}})
       (if image-url
         [:button.waves-effect.waves-light.btn.red
          {:on-click on-click-remove}
          "Bild löschen"]
         [:button.waves-effect.waves-light.btn
          {:on-click on-click}
          (when @(rf/subscribe [::product-form/machtfit-admin?])
            (str display-order ". "))
          "Bild auswählen"])]]]))

(defn- video []
  [:div {:style {:margin-top "2rem"}}
   [widgets/block-input2 {:id "video-url"
                          :label-text "Link zum video"
                          :value @(rf/subscribe [:get [:product :what :video-url]])
                          :on-save #(rf/dispatch [::set-video-url (or % "")])}]
   [:div>small "Bitte fügen Sie einen einbettbaren Link zu einem Video bei"]])

(defn- product-media []
  (let [images @(rf/subscribe [:get [:product :what :images]])
        first-image (first (filter #(= (:display-order %) 1) images))
        machtfit-admin? @(rf/subscribe [::product-form/machtfit-admin?])
        show-add-button? (< (count images) 3)]
    [:<>
     [image true (:image-id first-image) 1]
     (when machtfit-admin?
       [:<>
        (when (and (seq images) (> (count images) 1))
          (for [product-image (rest (sort-by :display-order images))]
            ^{:key (:id product-image)}
            [image true (:image-id product-image) (:display-order product-image)]))
        (when (and (seq images) show-add-button?)
          ^{:key (:id (gensym "new-product-image"))}
          [image true nil (inc (count images))])
        [video]])]))

(rf/reg-event-db ::add-attachment
  (fn [db [_ file-object]]
    (let [size (.-size file-object)
          size-in-megabytes (/ size 1024 1024)
          new-db (update db :ui dissoc :attachments-message)]
      (if (< 2 size-in-megabytes)
        (assoc-in new-db [:ui :attachments-message]
                  (str "Die Datei " (.-name file-object) " ist zu groß (" (ceil-to-decimal 2 size-in-megabytes) " MB). "
                       "Maximal sind 2 MB pro Anhang erlaubt."))
        (update-in new-db [:product :what :attachments] (fnil conj []) {:file-object file-object})))))

(defn remove-at
  [v i]
  (if (or (neg? i)
          (<= (count v) i))
    v
    (vec (concat (subvec v 0 i) (subvec v (inc i))))))

(defn attachment-is-new? [attachment]
  (not (:id attachment)))

(rf/reg-event-db ::remove-attachment
  (fn [db [_ i]]
    (let [attachment (get-in db [:product :what :attachments i])]
      (if (attachment-is-new? attachment)
        (update-in db [:product :what :attachments] remove-at i)
        (assoc-in db [:product :what :attachments i :delete?] true)))))

(rf/reg-event-db ::restore-attachment
  (fn [db [_ i]]
    (update-in db [:product :what :attachments i] dissoc :delete?)))

(defn process-raw-attachments [raw-attachments]
  (map
   (fn [{:keys [file-object] :as attachment}]
     (if (attachment-is-new? attachment)
       {:name (.-name file-object)
        :size (.-size file-object)}
       attachment))
   raw-attachments))

(rf/reg-sub ::attachments
  :<- [:get [:product :what :attachments]]
  (fn [raw-attachments _]
    (process-raw-attachments raw-attachments)))

(defn attachment-li [index {:keys [url name size delete?] :as attachment}]
  [:li {:key name}
   [:span
    {:style {:text-decoration (when delete? "line-through")}}
    (if (attachment-is-new? attachment)
      name
      [:a {:href url
           :target "blank"}
       name])
    " (" (ceil-to-decimal 2 (/ size 1024 1024)) " MB)"]
   " "
   (if delete?
     [:button
      {:on-click #(rf/dispatch [::restore-attachment index])}
      "wiederherstellen"]
     [:button
      {:on-click #(rf/dispatch [::remove-attachment index])}
      "entfernen"])])

(defn- attachments-list []
  (let [attachments-message-path [:ui :attachments-message]
        attachments @(rf/subscribe [::attachments])
        attachments-message @(rf/subscribe [:get [:ui :attachments-message]])]
    [:div {:style {:margin-top "1.5rem"}}
     [:div.input-field
      [:label.active {:style {:padding-bottom "0.4rem"}}
       "Zusatzinformationen für Interessenten (falls vorhanden)"]]
     [:div.s12
      [:ol
       (doall
        (for [[index attachment] (map-indexed vector attachments)]
          ^{:key index}
          [attachment-li index attachment]))]

      (when (< (count attachments) 5)
        [:input {:type "file"
                 :on-change #(do (rf/dispatch [::add-attachment
                                               (-> % .-target .-files (aget 0))])
                                 (-> % .-target .-value (set! nil)))}])]
     [:div.s12
      (when attachments-message
        [:div.col.s12>div.alert.alert-info.fade.in
         [:a.close {:on-click #(rf/dispatch [:set-path attachments-message-path nil])} "×"]
         [:div.alertinner.wicon
          [:i.icon-info-sign]
          attachments-message]])]]))

(def ^:private thumbnail-size {:width "151px" :height "121px"})

(defn- image-library-upload []
  (fileupload/fileupload {:id "image"
                          :input-element-id "image-upload-input"
                          :display-name "image-library-upload"
                          :param-name "image"
                          :label "Bild hochladen"
                          :accept "image/*"
                          :upload-url @(rf/subscribe [:get [:ui :add-image-url]])
                          :on-success #(rf/dispatch [::add-and-select-image
                                                     {:id (:pk %)
                                                      :url (:normal_thumb_url %)
                                                      :source (keyword (:source %))
                                                      :categories []
                                                      :thumbnail-url (:library_thumb_url %)}])}))

(defn- image-library-image [& {:keys [is-current? section-id machtfit-admin?]
                               {:keys [id thumbnail-url usages]} :image}]
  (let [item-id (str section-id "-" id)]
    ^{:key item-id}
    [:div.image
     {:class (when is-current? "current")
      :on-click #(rf/dispatch [::select-image id])}
     (when machtfit-admin?
       [:p (image-usage-text usages)])
     [:img
      {:src thumbnail-url
       :style thumbnail-size
       :id item-id}]]))

(defn- image-gallery [section-id category]
  [:div.image-gallery
   (let [current-image-id @(rf/subscribe [::current-image-id-by-position])
         machtfit-admin? @(rf/subscribe [::product-form/machtfit-admin?])
         images (if (some? category) @(rf/subscribe [::images-in-category category]) @(rf/subscribe [::partner-images]))]
     (doall
      (for [image' (sort (compare-by :source :desc :id :asc) images)]
        (image-library-image :image image'
                             :is-current? (= (:id image') current-image-id)
                             :section-id section-id
                             :machtfit-admin? machtfit-admin?))))])

(defn- image-section-expanded? [expanded current-category section-id]
  (cond
    (contains? expanded section-id) (expanded section-id)
    (some? current-category) (= section-id :current-category)
    :else (= section-id :partner-images)))

(rf/reg-sub ::image-section-expanded?
  :<- [:get [:ui :image-section-expanded]]
  :<- [:get [:product :what :category]]

  (fn [[expanded current-category] [_ section-id]]
    (image-section-expanded? expanded current-category section-id)))

(rf/reg-event-db ::toggle-section-expanded
  (fn [db [_ section-id]]
    (let [expanded (get-in db [:ui :image-section-expanded])
          current-category (get-in db [:product :what :category])
          expanded? (image-section-expanded? expanded current-category section-id)]
      (assoc-in db [:ui :image-section-expanded section-id] (not expanded?)))))

(defn- image-library-section [& {:keys [id category]}]
  (let [expanded? @(rf/subscribe [::image-section-expanded? id])
        images-in-category? (if (some? category) @(rf/subscribe [::images-in-category? category]) @(rf/subscribe [::partner-images?]))
        title' (or category "Meine Bilder")]
    (when images-in-category?
      [:div
       {:style {:clear "left"}}
       [:h2
        {:on-click #(rf/dispatch [::toggle-section-expanded id])
         :style {:cursor "pointer" :white-space "nowrap"}}
        [:i.material-icons
         {:style {:line-height "21px" :vertical-align "middle"}}
         (if expanded? "keyboard_arrow_down" "keyboard_arrow_right")]
        [:span {:style {:vertical-align "middle"}} title']]
       (when expanded?
         [image-gallery id category])])))

(rf/reg-sub ::image-categories
  :<- [:get [:categories]]
  :<- [:get [:images]]

  (fn [[product-categories images] _]
    (->> images
         (map :categories)
         seq
         flatten
         (apply sorted-set)
         (set/union product-categories))))

(defn- image-library []
  (let [current-image-id @(rf/subscribe [::current-image-id-by-position])
        ;; The category currently selected in the form.
        current-category @(rf/subscribe [:get [:product :what :category]])

        image-categories @(rf/subscribe [::image-categories])

        ;; Only those image categories that the partner has products in - these are shown earlier.
        partner-product-categories (disj @(rf/subscribe [:get [:partner-product-categories]]) current-category)

        ;; The rest of the categories - these are shown later
        other-categories (disj (set/difference image-categories partner-product-categories) current-category)

        has-partner-product-images? (some (fn [category]
                                            @(rf/subscribe [::images-in-category? category])) partner-product-categories)
        has-other-images? (some (fn [category]
                                  @(rf/subscribe [::images-in-category? category])) other-categories)]
    [:div#image-library-container
     [:div.image-library-content.card
      [:div.right>a.waves-effect.waves-light.btn.btn-flat
       {:on-click #(rf/dispatch [::toggle-image-library-active @(rf/subscribe [:get [:ui :current-image-position]])])}
       [:i.material-icons.left "cancel"]
       "Schließen"]
      [:div#image-library
       {:style {:clear "right"}}
       (list
        ^{:key "add-image"}
        [image-library-upload]

        ^{:key "current-image"}
        [:div.col.s4
         {:style {:float "right" :margin-top "4rem"}}
         [image false current-image-id nil]]

        (when (some? current-category)
          (let [section-id :current-category]
            ^{:key section-id}
            [image-library-section :id section-id :category current-category :image-key current-image-id]))

        (let [section-id :partner-images]
          ^{:key section-id}
          [image-library-section :id section-id])

        (when has-partner-product-images?
          ^{:key "partner-product-categories"}
          [:div
           {:style {:clear "left"}}
           [:h3
            {:style {:white-space "nowrap" :margin-top "2rem"}}
            "Kategorien Ihrer Angebote"]
           (doall
            (for [category partner-product-categories]
              (let [section-id (keyword (str "category-" category))]
                ^{:key section-id}
                [image-library-section :id section-id :category category])))])

        (when has-other-images?
          ^{:key "other-categories"}
          [:div
           {:style {:clear "left"}}
           [:h3
            {:style {:white-space "nowrap" :margin-top "2rem"}}
            "Weitere Kategorien"]
           (doall
            (for [category other-categories]
              (let [section-id (keyword (str "category-" category))]
                ^{:key section-id}
                [image-library-section :id section-id :category category])))]))]]]))

(defn- promoted []
  [:span
   [:input.filled-in {:id "promoted"
                      :type "checkbox"
                      :checked @(rf/subscribe [:get [:product :what :promoted?]])
                      :on-change #(rf/dispatch [:toggle [:product :what :promoted?]])}]
   [:label {:for "promoted" :style {:color "black"}}
    [:strong "Angebot empfehlen"]]
   [:div>small "erscheint oberhalb der Angebotsliste und wird hervorgehoben"]])

(defn- source-type []
  [:div.col.s12
   [:div.center-align
    (case @(rf/subscribe [:get [:product :what :source-type]])
      "form" "manuelle Eingabe über Formular"
      [:span "ZPP Kursnummer: " [:a {:href @(rf/subscribe [:get [:product :what :source-url]]) :target "_blank"}
                                 @(rf/subscribe [:get [:product :what :source-id]])]])]])

(rf/reg-event-db ::set-sorting-number
  (fn [db [_ new-sorting-number]]
    (-> db
        (assoc-in [:product :what :sorting-number] new-sorting-number)
        (assoc-in [:ui :last-focused-config-section] nil))))

(rf/reg-event-db ::set-video-url
  (fn [db [_ new-video-url]]
    (-> db
        (assoc-in [:product :what :video-url] new-video-url)
        (assoc-in [:ui :last-focused-config-section] nil))))

(defn- sorting-number []
  [:div {:style {:margin-top "2rem"}}
   [widgets/block-input2 {:id "sorting-number"
                          :label-text "Sortierzahl"
                          :value @(rf/subscribe [:get [:product :what :sorting-number]])
                          :on-save #(rf/dispatch [::set-sorting-number %])}]
   [:small "Angebote mit kleineren Zahlen erscheinen weiter oben"]])

(rf/reg-event-db
  :update-bookable
  (fn [db [_ updates]]
    (update-in db [:product :what] merge updates)))

(defn- bookable []
  (let [bookable? @(rf/subscribe [:get [:product :what :bookable?]])
        wellhub-promotion? @(rf/subscribe [:get [:product :what :wellhub-promotion?]])]
    [:div {:style {:padding-top "2rem"}}
     [:div.input-field
      [:label.active {:style {:padding-bottom "0.4rem"}} "Buchbarkeit"]]
     [widgets/radio-options
      {:name "bookable-options"
       :choices [[{:bookable? true
                   :wellhub-promotion? false} "Dies ist ein verbindliches, buchbares Angebot."]
                 [{:bookable? false
                   :wellhub-promotion? true} "Dies ist ein Wellhub Promotion Angebot"]
                 [{:bookable? false
                   :wellhub-promotion? false} "Dies Angebot ist unverbindlich und kann nur angefragt, aber nicht gebucht werden."]]
       :current-value {:bookable? bookable?
                       :wellhub-promotion? wellhub-promotion?}
       :on-select (fn [val]
                    (rf/dispatch
                     [:update-bookable val]))}]
     (when (and bookable? (not @(rf/subscribe [:get [:options :has-active-trainer?]])))
       [:div {:style {:font-size "1rem" :margin-top "0.5rem"}}
        [:i.material-icons.red-text "error_outline"]
        [:span {:style {:vertical-align "top"}}
         "Dieser Partner hat keine aktiven Trainer."]])]))

(rf/reg-event-db ::set-labels
  (fn [db [_ labels]]
    (assoc-in db [:product :what :labels] labels)))

(defn- labels []
  (let [open? (r/atom false)]
    (fn []
      [:div.col.s12
       [:p @open?]
       [:p "Labels zuweisen" [:a.waves-effect
                              {:on-click #(swap! open? not)
                               :style {:margin-left "30px"}}
                              [:i.material-icons.tiny (if @open? "expand_less" "expand_more")]
                              (if @open? "Zuklappen" "Aufklappen")]]
       (when @open?
         (doall
          (for [[group labels'] @(rf/subscribe [:get [:labels-with-groups]])]
            ^{:key group}
            [:div
             [:span {:style {:font-weight "bold"}}
              (if (= group :null) "Nicht zugeordnet" group)]
             (materialize-widgets/multi-select {:name "labels"
                                                :values @(rf/subscribe [:get [:product :what :labels]])
                                                :choices (sort labels')
                                                :on-change #(rf/dispatch [::set-labels %])})])))])))

(rf/reg-sub ::content-sections
  :<- [:get [:product :what :content-sections]]
  (fn [content-sections _]
    content-sections))

(rf/reg-event-db ::add-content-section
  (fn [db [_]]
    (update-in db [:product :what :content-sections] #(conj (if (empty? %) [] %) {:title ""}))))

(rf/reg-event-db ::delete-content-section
  (fn [db [_ index]]
    (update-in db [:product :what :content-sections] delete-at index)))

(rf/reg-event-db ::add-content-section-faq
  (fn [db [_ index]]
    (update-in db [:product :what :content-sections index :faqs] #(conj (if (empty? %) [] %) {:title "" :text ""}))))

(rf/reg-event-db ::delete-content-section-faq
  (fn [db [_ content-section-index index]]
    (update-in db [:product :what :content-sections content-section-index :faqs] delete-at index)))

(defn- faq-text-too-big-warning [path]
  (when @(rf/subscribe [::product-form/text-too-big? path])
    [:span>i {:style {:color "red"}}
     "Der FAQ-Text ist zu groß. Benutzen Sie ggfs. besser komprimierte, weniger oder kleinere Bilder.
      Alle Bilder zusammen sollten nicht größer als ungefähr 750 KB sein."]))

(defn- faqs-total-too-big-warning []
  (when @(rf/subscribe [::product-form/faqs-total-too-big?])
    [:span>i {:style {:color "red"}}
     "Alle FAQ-Texte zusammen sind zu groß. Benutzen Sie ggfs. besser komprimierte, weniger oder kleinere Bilder.
      Alle Bilder in allen FAQs zusammen sollten nicht größer als ungefähr 2 MB sein."]))

(defn- content-section-faq-row [content-section-index index {title' :title text :text}]
  [:div {:style {:margin-bottom "24px"
                 :border "1px solid #ccc"
                 :background "white"
                 :padding "24px 16px"}}
   [:div.col.s12 {:style {:margin-bottom "1rem"}}
    [widgets/block-input
     {:id (str "faq-" content-section-index index)
      :value title'
      :on-change #(rf/dispatch [:set-path [:product :what :content-sections content-section-index :faqs index :title] %])
      :block-style {:width "100%"}
      :style {:width "100%"}
      :required? true}]
    (when (> (count title') 255)
      [:span>i {:style {:color "red"}}
       "Der Titel ist zu lang. Es dürfen nur maximal 255 Zeichen verwendet werden."])
    [widgets/quill
     {:value text
      :on-change #(rf/dispatch [:set-path [:product :what :content-sections content-section-index :faqs index :text] %])}]
    [faq-text-too-big-warning [:product :what :content-sections content-section-index :faqs index :text]]]
   [:div
    [:button {:style {:border 0 :text-decoration "underline" :background "white"}
              :on-click #(rf/dispatch [::delete-content-section-faq content-section-index index])}
     "Eintrag löschen"]]])

(defn- content-section-row [index {title' :title faqs :faqs}]
  [:div {:style {:margin-bottom "24px"
                 :border "1px solid #ccc"
                 :background "white"
                 :padding "24px 16px"}}
   [:div.col.s12 {:style {:margin-bottom "1rem"}}
    [widgets/block-input
     {:id (str "Sektion-" index)
      :label-text "Titel"
      :value title'
      :on-change #(rf/dispatch [:set-path [:product :what :content-sections index :title] %])
      :block-style {:width "100%"}
      :style {:width "100%"}
      :required? true}]
    (when (> (count title') 255)
      [:span>i {:style {:color "red"}}
       "Der Titel ist zu lang. Es dürfen nur maximal 255 Zeichen verwendet werden."])]
   (when faqs
     [:div.col.s12
      (for [[idx faq] (map-indexed vector faqs)]
        ^{:key idx}
        [content-section-faq-row
         index idx faq])])
   [:div {:style {:margin-bottom "24px"}}
    [widgets/add-button {:title "Eintrag hinzufügen"
                         :on-click #(rf/dispatch [::add-content-section-faq index])}]]
   [:div
    [:button {:style {:border 0 :text-decoration "underline" :background "white"}
              :on-click #(rf/dispatch [::delete-content-section index])}
     "Ausklappbaren Inhalt löschen"]]])

(defn content-section-table []
  (let [content-sections @(rf/subscribe [::content-sections])]
    [:div {:style {:padding-top "2rem" :padding-bottom "1rem"}}
     [:div.input-field
      [:label.active {:style {:padding-bottom "0.4rem"}}
       "Ausklappbarer Inhalt"]
      (when content-sections
        (for [[idx content-section] (map-indexed vector content-sections)]
          ^{:key idx}
          [content-section-row idx content-section]))
      [:div
       [widgets/add-button {:title "Ausklappbaren Inhalt hinzufügen"
                            :on-click #(rf/dispatch [::add-content-section])}]]]]))

(rf/reg-sub ::faqs
  :<- [:get [:product :what :faqs]]
  (fn [faqs _]
    faqs))

(rf/reg-event-db ::add-faq
  (fn [db [_]]
    (update-in db [:product :what :faqs] #(conj (if (empty? %) [] %) {:title "" :text ""}))))

(rf/reg-event-db ::delete-faq
  (fn [db [_ index]]
    (update-in db [:product :what :faqs] delete-at index)))

(defn faq []
  (let [faqs @(rf/subscribe [::faqs])]
    [:div {:style {:padding-top "2rem" :padding-bottom "1rem"}}
     [:div.input-field
      [:label.active {:style {:padding-bottom "0.4rem"}}
       "FAQ"]
      (when (and faqs (not-empty faqs))
        [:div {:style {:margin-bottom "24px"
                       :border "1px solid #ccc"
                       :background "white"
                       :padding "24px 16px"}}
         (for [[idx faq'] (map-indexed vector faqs)]
           ^{:key idx}
           [:<>
            [:div.col.s12 {:style {:margin-bottom "1rem"}}
             [widgets/block-input
              {:id (str "faq-" idx)
               :value (:title faq')
               :on-change #(rf/dispatch [:set-path [:product :what :faqs idx :title] %])
               :block-style {:width "100%"}
               :style {:width "100%"}
               :required? true}]
             (when (> (count (:title faq')) 255)
               [:span>i {:style {:color "red"}}
                "Der Titel ist zu lang. Es dürfen nur maximal 255 Zeichen verwendet werden."])
             [widgets/quill
              {:value (:text faq')
               :on-change #(rf/dispatch [:set-path [:product :what :faqs idx :text] %])}]
             [faq-text-too-big-warning [:product :what :faqs idx :text]]]
            [:div
             [:button {:style {:border 0 :text-decoration "underline" :background "white"}
                       :on-click #(rf/dispatch [::delete-faq idx])}
              "Eintrag löschen"]]])])
      [:div
       [widgets/add-button {:title "Eintrag hinzufügen"
                            :on-click #(rf/dispatch [::add-faq])}]]
      [faqs-total-too-big-warning]]]))

(defn section-what []
  (let [machtfit-admin? @(rf/subscribe [::product-form/machtfit-admin?])
        auto-actions [[::set-title "Tolles Angebot!"]]]
    [widgets/section {:anchor "what"
                      :title [review/highlighted-section-title :what "Was"]
                      :subtitle [:div
                                 [:span "bieten Sie an?" [:br]
                                  (when ^boolean js/goog.DEBUG [:button {:on-click #(dorun (map rf/dispatch auto-actions))}
                                                                "Auto"])]

                                 [review/field-badges :what]]
                      :content (list
                                ^{:key "image-media-library"}
                                (when @(rf/subscribe [:get [:ui :image-media-library-active?]])
                                  [image-library])

                                (-> ^{:key "what-section"} [:div]
                                    (into (when @(rf/subscribe [:get [:options :can-see-source-type?]]) [[source-type]]))
                                    (into (when machtfit-admin? [[partner]]))
                                    (into [[title]
                                           (when machtfit-admin? [offer-layers-toggle])
                                           (when machtfit-admin?
                                             [:div.col.s12
                                              [offer-layers/section-offer-layers]])

                                           [:div.col.s8
                                            (when (and machtfit-admin? @(rf/subscribe [:get [:options :partner-product?]])) [bookable])
                                            [categories]
                                            [search-keywords]
                                            [ai-search-keywords]
                                            [key-facts]
                                            [description]
                                            (when machtfit-admin? [health-relevance-text])
                                            [content-section-table]
                                            [faq]]
                                           [:div.col.s4 [product-media]
                                            (when @(rf/subscribe [:get [:options :can-promote?]]) [promoted])
                                            (when @(rf/subscribe [:get [:options :has-sorting-number?]]) [sorting-number])]
                                           [website :what]
                                           [:div.col.s12 [attachments-list]]
                                           (when (and machtfit-admin? @(rf/subscribe [:get [:options :partner-product?]])) [labels])])
                                    (conj [show-next-section :what])))}]))
